import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Button as ButtonUnstyled } from '@mui/base/Button';
import cx from 'classnames';
import { Box, Stack } from 'src/components-v2/Layout';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events } from 'src/lib/trackingEvents';
import styled from 'threads5/themes/styled';
import { IBtnProps } from './Btn.types';
import { useLocalizedHref } from './useLocalizedHref';

const StyledPrimaryButton = styled(ButtonUnstyled)(({ theme }) => {
  return [
    {
      alignItems: 'center',
      border: 'none',
      display: 'inline-flex',
      gap: 1,
      fontFamily: theme.typography.sans,
      fontWeight: 600,
      height: 'auto',
      letterSpacing: '-0.75px',
      lineHeight: '130%',
      color: `${theme.palette['web3/component/button/primary/resting-text']} !important`,
      whiteSpace: 'nowrap',
      padding: 0,
      borderRadius: '100px !important',
      overflow: 'hidden',
      textDecoration: 'none',
      outline: 'none',
      '--btn-surface-height': '48px',
      '--btn-surface-padding-x': '24px',
      '--btn-surface-width': '100%',
      '@keyframes spin': {
        from: {
          transform: 'translate(-50%, -50%) rotate(0deg)',
        },
        to: {
          transform: 'translate(-50%, -50%) rotate(360deg)',
        },
      },
      [theme.breakpoints.up('xs')]: {
        fontSize: '1.8rem',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '2.0rem',
      },
      '&:hover': {
        boxShadow: theme.palette['web3/component/button/primary/hovered-glow'],
      },
      '&:active': {
        boxShadow: 'none',
      },
      '&:focus-visible': {
        outlineWidth: '2px',
        outlineColor: theme.palette['web3/component/button/outline'],
        outlineStyle: 'solid',
        outlineOffset: '3px',
      },
      '& .btn-surface': {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        height: 'var(--btn-surface-height)',
        padding: '0 var(--btn-surface-padding-x)',
        borderRadius: 'inherit',
        width: 'var(--btn-surface-width)',
      },
      [theme.breakpoints.up('xl')]: {
        '--btn-surface-height': '56px',
        '& .btn-surface': {
          lineHeight: '150%',
        },
      },
      '& .bg-c': {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        borderRadius: 'inherit',
        boxShadow: theme.palette['web3/component/button/primary/box-shadow'],
      },
      '& .ring': {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        borderRadius: 'inherit',
        overflow: 'hidden', // clip the oversized rotating gradient square to the button bounds
        padding: '3px', // using 3px vs 2px to try and cover some edge cases where theres some color bleed through the gap between the elements
        boxSizing: 'border-box',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        // Paint the conic gradient once with a static angle and spin the element
        // via transform so the rotation is composited on the GPU instead of
        // repainting the gradient every frame on the CPU.
        '&:before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '200%',
          aspectRatio: '1',
          transform: 'translate(-50%, -50%)',
          background:
            'conic-gradient(in oklch longer hue, oklch(0.91 0.19 69.4) 0deg 360deg)',
          animation: 'spin 2.5s infinite linear',
          willChange: 'transform',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '&::before': {
            animation: 'none',
          },
        },
      },
      '& .bg': {
        content: '""',
        position: 'absolute',
        top: '2px',
        left: '2px',
        right: '2px',
        bottom: '2px',
        background:
          theme.palette['web3/component/button/primary/background-resting'],
        borderRadius: 'inherit',
        mixBlendMode: 'luminosity',
      },
      '& .txt': {
        position: 'relative',
      },
      '&:hover .bg-c, &:active .bg-c': {
        mixBlendMode: 'luminosity',
        boxShadow: 'none',
      },
      '&:active .bg-c, &:active .txt': {
        transition: 'transform 0.1s ease',
        transform: 'scale(0.98)',
      },
      '&:hover .txt, &:active .txt': {
        color: `${theme.palette['web3/component/button/primary/interacted-text']} !important`,
      },
    },
  ];
});

const BtnPrimary = ({
  backgroundColor,
  children,
  defaultGap,
  iconName,
  iconSize,
  isInverted,
  leftIcon,
  rightIcon,
  sys,
  trackingId,
  variant,
  className,
  ...props
}: IBtnProps) => {
  const track = useTrack();
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const { href, ...buttonProps } = props;
  const localizedHref = useLocalizedHref(href);

  const handleClick = (event) => {
    if (trackingId) {
      track({
        type: Events.BUTTON_CLICKED,
        payload: { trackingId, event, currentTarget: event.currentTarget },
      });
    }
    if (buttonProps.onClick && !href) {
      buttonProps.onClick(event);
    }
  };

  return (
    <StyledPrimaryButton
      {...buttonProps}
      href={localizedHref}
      slots={{ root: href ? 'a' : 'button' }}
      className={cx(className, 'cms-btn-primary')}
      onClick={handleClick}
      {...inspectorProps({ fieldId: 'text' })}
    >
      <Box component='span' className='btn-surface'>
        <Box component='span' className='bg-c'>
          <Box component='span' className='ring' />
          <Box component='span' className='bg' />
        </Box>
        <Stack
          gap={1.25}
          direction='row'
          component='span'
          className='txt'
          alignItems='center'
          justifyContent='center'
          sx={{
            '.btn-icon': {
              color: 'graphic/inverse',
            },
            width: '100%',
            '.icon-container': {
              display: 'flex',
            },
          }}
        >
          {leftIcon} {children} {rightIcon}
        </Stack>
      </Box>
    </StyledPrimaryButton>
  );
};

export default BtnPrimary;
